import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useUser } from '@clerk/clerk-react';
import { userApplicationService } from '../services/api/userApplicationService';
import {
  useApplicationsQuery,
  useCreateApplicationMutation,
  useCancelApplicationMutation,
} from '../hooks/queries/applications';

// Custom event for application changes that other contexts can listen to
const APPLICATION_CHANGED_EVENT = 'application_changed';

// Helper to dispatch the application changed event
function dispatchApplicationChangedEvent(applicationId, threadId, trademarkId) {
  const event = new CustomEvent(APPLICATION_CHANGED_EVENT, {
    detail: { applicationId, threadId, trademarkId }
  });
  window.dispatchEvent(event);
  console.log('Dispatched application changed event:', { applicationId, threadId, trademarkId });
}

// Initial state
const initialState = {
  currentApplicationId: sessionStorage.getItem('currentApplicationId') || null,
  currentThreadId: (() => {
    const stored = sessionStorage.getItem('currentThreadId');
    return (stored && stored !== 'null') ? stored : null;
  })(),
  applicationMode: sessionStorage.getItem('applicationMode') || 'new', // 'new' or 'existing'
  loading: false,
  error: null
};

// Actions
const actions = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_APPLICATION: 'SET_CURRENT_APPLICATION',
  SET_APPLICATION_MODE: 'SET_APPLICATION_MODE',
  RESET: 'RESET'
};

// Reducer
function userApplicationReducer(state, action) {
  switch (action.type) {
    case actions.SET_LOADING:
      return { ...state, loading: action.payload };
    case actions.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case actions.SET_CURRENT_APPLICATION:
      // Store in sessionStorage when setting current application
      sessionStorage.setItem('currentApplicationId', action.payload.applicationId);
      // Only store threadId if it's not null to avoid storing "null" string
      if (action.payload.threadId) {
        sessionStorage.setItem('currentThreadId', action.payload.threadId);
      } else {
        sessionStorage.removeItem('currentThreadId');
      }
      if (action.payload.mode) {
        sessionStorage.setItem('applicationMode', action.payload.mode);
      }
      
      return { 
        ...state, 
        currentApplicationId: action.payload.applicationId,
        currentThreadId: action.payload.threadId,
        applicationMode: action.payload.mode || state.applicationMode,
        loading: false 
      };
    case actions.SET_APPLICATION_MODE:
      // Store in sessionStorage when setting application mode
      sessionStorage.setItem('applicationMode', action.payload);
      return { ...state, applicationMode: action.payload };
    case actions.RESET:
      return initialState;
    default:
      return state;
  }
}

// Create context
const UserApplicationContext = createContext();

// Provider component
export function UserApplicationProvider({ children }) {
  const [state, dispatch] = useReducer(userApplicationReducer, initialState);
  const { user, isSignedIn } = useUser();
  const createApplicationMutation = useCreateApplicationMutation();
  const cancelApplicationMutation = useCancelApplicationMutation();
  const {
    data: applicationsData,
    isLoading: applicationsQueryLoading,
    isFetching: applicationsQueryFetching,
    error: applicationsQueryError,
    refetch: refetchApplications,
  } = useApplicationsQuery(user?.id);
  const applications = applicationsData || [];
  const applicationsLoading = applicationsQueryLoading || applicationsQueryFetching;
  const applicationsError = applicationsQueryError;

  useEffect(() => {
    if (!isSignedIn) {
      dispatch({ type: actions.RESET });
    }
  }, [isSignedIn]);

  const setCurrentApplication = useCallback(
    ({ applicationId, threadId, mode, trademarkId }) => {
      dispatch({
        type: actions.SET_CURRENT_APPLICATION,
        payload: { applicationId, threadId, mode },
      });

      const fallbackTrademarkId =
        trademarkId ||
        applications.find((app) => app.id === applicationId)?.trademarkId ||
        null;

      dispatchApplicationChangedEvent(applicationId, threadId, fallbackTrademarkId);
    },
    [applications]
  );

  useEffect(() => {
    if (
      !applicationsLoading &&
      !state.currentApplicationId &&
      applications.length > 0 &&
      !applicationsError
    ) {
      // Auto-select the most recently updated application so multi-app users
      // land on the live one (the API returns applications unordered)
      const sortedApps = [...applications].sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt || 0) -
          new Date(a.updatedAt || a.createdAt || 0)
      );
      const firstApp = sortedApps[0];
      setCurrentApplication({
        applicationId: firstApp.id,
        threadId: firstApp.threadId,
        mode: state.applicationMode,
        trademarkId: firstApp.trademarkId,
      });
    }
  }, [
    applications,
    applicationsError,
    applicationsLoading,
    setCurrentApplication,
    state.applicationMode,
    state.currentApplicationId,
  ]);
  
  const loadUserApplications = useCallback(async () => {
    return refetchApplications();
  }, [refetchApplications]);
  
  // Create a new application
  const createNewApplication = useCallback(
    async (trademarkId = null, applicationName = null) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      dispatch({ type: actions.SET_LOADING, payload: true });
      dispatch({ type: actions.SET_APPLICATION_MODE, payload: 'new' });
      
      try {
        const payload = {
          userId: user.id,
          trademarkId,
          applicationName,
        };
        const newApplication = await createApplicationMutation.mutateAsync(payload);

        await refetchApplications();

        const applicationId = newApplication.id || newApplication.applicationId;
        const threadId = newApplication.threadId || null;

        setCurrentApplication({
          applicationId,
          threadId,
          mode: 'new',
          trademarkId: newApplication.trademarkId || trademarkId || null,
        });

        return newApplication;
      } catch (error) {
        console.error('Failed to create application', error);
        dispatch({ type: actions.SET_ERROR, payload: error.message });
        throw error;
      } finally {
        dispatch({ type: actions.SET_LOADING, payload: false });
      }
    },
    [createApplicationMutation, refetchApplications, setCurrentApplication, user?.id]
  );

  // Cancel an application
  const cancelApplication = useCallback(
    async (applicationId) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      dispatch({ type: actions.SET_LOADING, payload: true });

      try {
        await cancelApplicationMutation.mutateAsync({ applicationId });
        
        await refetchApplications();

        // If current application was cancelled, reset current? 
        // No, keep it selected but user will see it's cancelled status.
      } catch (error) {
        console.error('Failed to cancel application', error);
        dispatch({ type: actions.SET_ERROR, payload: error.message });
        throw error;
      } finally {
        dispatch({ type: actions.SET_LOADING, payload: false });
      }
    },
    [cancelApplicationMutation, refetchApplications, user?.id]
  );
  
  // Load a specific application
  const loadApplication = useCallback(
    async (applicationId) => {
      if (!applicationId) return;
      
      dispatch({ type: actions.SET_LOADING, payload: true });
      dispatch({ type: actions.SET_APPLICATION_MODE, payload: 'existing' });
      
      try {
        const applicationData = await userApplicationService.getApplicationData(applicationId);
        
        const threadId =
          applicationData.threadId ||
          applicationData.application?.threadId ||
          null;
        const trademarkId =
          applicationData.trademarkId ||
          applicationData.trademark?.id ||
          null;
        
        setCurrentApplication({
          applicationId,
          threadId,
          mode: 'existing',
          trademarkId,
        });
        
        return applicationData;
      } catch (error) {
        console.error(`Failed to load application ${applicationId}`, error);
        dispatch({ type: actions.SET_ERROR, payload: error.message });
        throw error;
      } finally {
        dispatch({ type: actions.SET_LOADING, payload: false });
      }
    },
    [setCurrentApplication]
  );
  
  // Helper to determine if we should create new application or update existing
  const shouldCreateNewApplication = useCallback(() => {
    return state.applicationMode === 'new' || !state.currentApplicationId;
  }, [state.applicationMode, state.currentApplicationId]);
  
  // Helper to get current application context for trademark upload
  const getApplicationContext = useCallback(() => {
    return {
      applicationId: state.currentApplicationId,
      threadId: state.currentThreadId,
      mode: state.applicationMode,
      shouldCreateNew: shouldCreateNewApplication()
    };
  }, [
    shouldCreateNewApplication,
    state.applicationMode,
    state.currentApplicationId,
    state.currentThreadId
  ]);

  const currentApplication = useMemo(() => {
    if (!state.currentApplicationId) {
      return null;
    }
    return applications.find((app) => app.id === state.currentApplicationId) || null;
  }, [applications, state.currentApplicationId]);

  // 🔄 FORCE REFRESH: Reload applications data from backend (bypasses cache)
  const refreshApplications = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    await refetchApplications();
  }, [refetchApplications, user?.id]);

  const combinedLoading = state.loading || applicationsLoading;
  const combinedError = state.error || applicationsError?.message || null;

  // Value to be provided - MEMOIZED to prevent infinite remounts
  const contextValue = useMemo(() => ({
    applications,
    currentApplication,
    currentApplicationId: state.currentApplicationId,
    currentThreadId: state.currentThreadId,
    applicationMode: state.applicationMode,
    loading: combinedLoading,
    error: combinedError,
    loadUserApplications,
    loadApplication,
    createNewApplication,
    cancelApplication,
    shouldCreateNewApplication,
    getApplicationContext,
    refreshApplications,
    setCurrentApplication,
  }), [
    applications,
    combinedError,
    combinedLoading,
    createNewApplication,
    cancelApplication,
    currentApplication,
    getApplicationContext,
    loadApplication,
    loadUserApplications,
    refreshApplications,
    setCurrentApplication,
    state.currentApplicationId,
    state.currentThreadId,
    state.applicationMode,
    shouldCreateNewApplication
  ]);
  
  return (
    <UserApplicationContext.Provider value={contextValue}>
      {children}
    </UserApplicationContext.Provider>
  );
}

// Custom hook for using the context
export function useUserApplication() {
  const context = useContext(UserApplicationContext);
  
  if (!context) {
    throw new Error('useUserApplication must be used within a UserApplicationProvider');
  }
  
  return context;
}
